import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Button, Descriptions, Modal, Tag } from 'antd';
import clsx from 'clsx';
import React, { useContext, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../AppContext';
import { BREAKPOINTS } from '../../common/constants';
import { SEND_VERIFICATION_LINK } from '../profile/graphql/Mutation';
import { GET_PROFILE } from '../users/graphql/Queries';

export default function VerifyDetailsModal({ openPrompt }) {
  const { dispatch, getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const [successMessage, setSuccessMessage] = useState();
  const [showPrompt, setShowPrompt] = useState(false);
  const isMobileViewport = useMedia(`(max-width: ${BREAKPOINTS.mobile}px)`);
  const { initializeAuth } = useContext(AppContext);

  useMemo(() => {
    setShowPrompt(openPrompt);
  }, [openPrompt]);

  const [getLoggedInUser, { loading: getProfileLoading }] = useLazyQuery(
    GET_PROFILE,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (res) => {
        initializeAuth('', '', res?.getLoggedInUser);
      },
      onError() {},
    },
  );

  const [sendVerificationLink, { loading }] = useMutation(
    SEND_VERIFICATION_LINK,
    {
      onError() {},
      onCompleted(res) {
        setSuccessMessage(res?.sendVerificationLink?.message);
      },
    },
  );

  const handleOK = async () => {
    sendVerificationLink({
      variables: {
        where: {
          userId: currentUser?.id,
        },
      },
    });
  };

  const handleCancel = async () => {
    await getLoggedInUser();
    dispatch({ type: 'SET_SHOW_VERIFY_DETAILS_MODAL', data: false });
    setShowPrompt(false);
  };

  return showPrompt ? (
    <div id="router-prompt">
      <Modal
        open={showPrompt}
        footer={null}
        closable={false}
        destroyOnClose
        maskClosable={false}
        centered
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('router-prompt')
        }
      >
        {successMessage && (
          <Alert
            message={<h3>Verification Link Sent</h3>}
            description={successMessage}
            type="success"
            className="mb-10"
            showIcon
          />
        )}
        <h2>Verify Contact Details</h2>
        <p>
          {!currentUser?.emailVerified && !currentUser?.numberVerified
            ? 'Please verify your email id and mobile number details for secure access.'
            : `Please verify your ${!currentUser?.emailVerified ? 'email id' : 'mobile number'} for secure access.
`}
        </p>
        <Descriptions column={1}>
          <Descriptions.Item label="Name">
            {currentUser?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <div
              className={clsx(
                'd-flex align-center',
                isMobileViewport && 'flex-vertical',
              )}
            >
              {currentUser?.email}
              {!currentUser?.emailVerified && (
                <Tag className="yellow-tag ml-5">Unverified</Tag>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Phone Number">
            <div
              className={clsx(
                'd-flex align-center',
                isMobileViewport && 'flex-vertical',
              )}
            >
              {currentUser?.phoneNo}
              {!currentUser?.numberVerified && (
                <Tag className="yellow-tag ml-5">Unverified</Tag>
              )}
            </div>
          </Descriptions.Item>
        </Descriptions>
        {!currentUser?.emailVerified && successMessage && (
          <div className="text-danger note-text mt-10">
            Note : If you didn’t receive the verification links on email id then
            please check your spam folder.
          </div>
        )}
        <div className="form-buttons mt-10">
          {successMessage ? (
            <Button
              shape="round"
              type="primary"
              className="save-button"
              onClick={handleCancel}
              loading={loading || getProfileLoading}
            >
              Okay
            </Button>
          ) : (
            <>
              <Button
                shape="round"
                className="cancel-button"
                onClick={handleCancel}
                disabled={loading}
              >
                Later
              </Button>
              <Button
                shape="round"
                type="primary"
                className="save-button"
                onClick={handleOK}
                loading={loading}
              >
                {!currentUser?.emailVerified && !currentUser?.numberVerified
                  ? 'Verify'
                  : `Verify ${!currentUser?.emailVerified ? 'Email' : 'Number'}`}
              </Button>
            </>
          )}
        </div>
      </Modal>
    </div>
  ) : null;
}
